import { useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import { fetchShareholderUsers, updateShareholderUserPermissions } from '../shareholderUsersApi';
import { useLanguage } from '../i18n/language';
import './BranchesPage.css';
import './Modal.css';
import './PermissionsPage.css';

function buildPermissionModules(isAr) {
  return [
    {
      id: 'shareholder_dashboard',
      label: isAr ? 'لوحة التحكم' : 'Dashboard',
      icon: 'dashboard',
      actions: [{ id: 'view', label: isAr ? 'عرض' : 'View' }],
      uiItems: [{ id: 'charts', label: isAr ? 'المخططات' : 'Charts' }],
    },
    {
      id: 'shareholder_decisions',
      label: isAr ? 'القرارات' : 'Decisions',
      icon: 'decisions',
      actions: [{ id: 'view', label: isAr ? 'عرض القرارات' : 'View decisions' }],
      uiItems: [{ id: 'details', label: isAr ? 'عرض التفاصيل' : 'View details' }],
    },
    {
      id: 'shareholder_meetings',
      label: isAr ? 'اجتماعاتي' : 'My meetings',
      icon: 'calendar',
      actions: [{ id: 'view', label: isAr ? 'عرض الاجتماعات' : 'View meetings' }],
      uiItems: [{ id: 'join_btn', label: isAr ? 'زر دخول الاجتماع' : 'Join meeting button' }],
    },
    {
      id: 'shareholder_ownership',
      label: isAr ? 'حصتي في الشركة' : 'My ownership',
      icon: 'money',
      actions: [{ id: 'view', label: isAr ? 'عرض الملكية' : 'View ownership' }],
      uiItems: [{ id: 'share_table', label: isAr ? 'جدول كبار المساهمين' : 'Major shareholders table' }],
    },
    {
      id: 'shareholder_dividends',
      label: isAr ? 'الأرباح الموزعة' : 'Distributed dividends',
      icon: 'chart',
      actions: [{ id: 'view', label: isAr ? 'عرض الأرباح' : 'View dividends' }],
      uiItems: [{ id: 'div_table', label: isAr ? 'جدول الدفعات' : 'Payments table' }],
    },
    {
      id: 'shareholder_users',
      label: isAr ? 'المستخدمين' : 'Users',
      icon: 'user',
      actions: [{ id: 'manage', label: isAr ? 'إدارة المستخدمين' : 'Manage users' }],
      uiItems: [{ id: 'create_form', label: isAr ? 'نموذج الإضافة' : 'Create form' }],
    },
    {
      id: 'shareholder_permissions',
      label: isAr ? 'الصلاحيات' : 'Permissions',
      icon: 'lock',
      actions: [{ id: 'manage', label: isAr ? 'إدارة الصلاحيات' : 'Manage permissions' }],
      uiItems: [{ id: 'matrix', label: isAr ? 'مصفوفة الصلاحيات' : 'Permissions matrix' }],
    },
  ];
}

const PERM_SHAPE = buildPermissionModules(false);

function emptyPermissions() {
  const o = {};
  PERM_SHAPE.forEach((m) => {
    o[m.id] = { pageAccess: false, actions: {}, ui: {} };
    m.actions.forEach((a) => { o[m.id].actions[a.id] = false; });
    m.uiItems.forEach((u) => { o[m.id].ui[u.id] = false; });
  });
  return o;
}

function normalize(raw) {
  const base = emptyPermissions();
  if (!raw || typeof raw !== 'object') return base;
  PERM_SHAPE.forEach((m) => {
    base[m.id].pageAccess = !!raw[m.id]?.pageAccess;
    m.actions.forEach((a) => { base[m.id].actions[a.id] = !!raw[m.id]?.actions?.[a.id]; });
    m.uiItems.forEach((u) => { base[m.id].ui[u.id] = !!raw[m.id]?.ui?.[u.id]; });
  });
  return base;
}

export default function ShareholderPermissionsPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useMemo(() => ({
    title: isAr ? 'صلاحيات المستخدمين (المساهم)' : 'User permissions (Shareholder)',
    subtitle: isAr ? 'إدارة الصلاحيات ضمن مساحة المساهم' : 'Manage permissions in the shareholder area',
    save: isAr ? 'حفظ الصلاحيات' : 'Save permissions',
    loading: isAr ? 'جارٍ التحميل...' : 'Loading...',
    selectUser: isAr ? 'اختر مستخدماً' : 'Select a user',
    search: isAr ? 'بحث...' : 'Search...',
    username: isAr ? 'اسم المستخدم' : 'Username',
    pageAccess: isAr ? 'الوصول للصفحة' : 'Page access',
    actions: isAr ? 'الإجراءات' : 'Actions',
    uiItems: isAr ? 'عناصر الواجهة' : 'UI items',
    saved: isAr ? 'تم حفظ الصلاحيات' : 'Permissions saved',
    saveFailed: isAr ? 'فشل الحفظ' : 'Save failed',
  }), [isAr]);

  const MODULES = useMemo(() => buildPermissionModules(isAr), [isAr]);

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [permissionsByUser, setPermissionsByUser] = useState({});
  const [expandedModule, setExpandedModule] = useState(() => PERM_SHAPE[0].id);
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const rows = await fetchShareholderUsers();
        setUsers(rows);
        if (rows[0]) setSelectedUserId(rows[0].id);
        const map = {};
        rows.forEach((u) => { map[u.id] = normalize(u.permissions); });
        setPermissionsByUser(map);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = userSearch.trim();
    if (!q) return users;
    return users.filter((u) => u.userName.includes(q) || (u.fullName || '').includes(q));
  }, [users, userSearch]);

  const selected = users.find((u) => u.id === selectedUserId);
  const permissions = permissionsByUser[selectedUserId] ?? emptyPermissions();

  const mutate = (moduleId, cb) => {
    setPermissionsByUser((prev) => {
      const up = prev[selectedUserId] ? { ...prev[selectedUserId] } : emptyPermissions();
      const mp = { ...up[moduleId], actions: { ...up[moduleId].actions }, ui: { ...up[moduleId].ui } };
      cb(mp);
      up[moduleId] = mp;
      return { ...prev, [selectedUserId]: up };
    });
  };

  const save = async () => {
    if (!selectedUserId) return;
    try {
      await updateShareholderUserPermissions(selectedUserId, permissionsByUser[selectedUserId] ?? emptyPermissions());
      setMsg(t.saved);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : t.saveFailed);
    }
    setTimeout(() => setMsg(''), 2500);
  };

  return (
    <div className="page perm-page">
      {msg && <div className="perm-toast perm-toast--ok">{msg}</div>}
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="lock" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t.title}</span>
            <span className="list-page-card__sub">{t.subtitle}</span>
          </div>
          <div className="list-page-card__actions"><button type="button" className="btn btn--primary" onClick={save} disabled={!selectedUserId}>{t.save}</button></div>
        </header>

        {loading ? <div className="list-page-card__loading">{t.loading}</div> : (
          <div className="perm-layout">
          <aside className="perm-users">
            <div className="perm-users__head">
              <span className="perm-users__title">{t.selectUser}</span>
              <div className="perm-users__search"><Icon name="search" size={14} color="#90a4ae" /><input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder={t.search} /></div>
            </div>
            <ul className="perm-users__list">
              {filtered.map((u) => (
                <li key={u.id}>
                  <button type="button" className={`perm-user-card${selectedUserId === u.id ? ' perm-user-card--active' : ''}`} onClick={() => setSelectedUserId(u.id)}>
                    <div className="perm-user-card__avatar">{(u.fullName || u.userName || '?')[0]}</div>
                    <div className="perm-user-card__body"><div className="perm-user-card__name">{u.fullName || '—'}</div><div className="perm-user-card__meta">{u.userName}</div><div className="perm-user-card__email">{u.email || '—'}</div></div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section className="perm-matrix">
            {selected && (
              <div className="perm-matrix__banner">
                <div className="perm-matrix__banner-icon"><Icon name="user" size={22} color="#fff" /></div>
                <div>
                  <div className="perm-matrix__banner-name">{selected.fullName || '—'}</div>
                  <div className="perm-matrix__banner-sub">{t.username}: {selected.userName}</div>
                </div>
              </div>
            )}
            <div className="perm-accordions">
              {MODULES.map((mod) => {
                const p = permissions[mod.id];
                const open = expandedModule === mod.id;
                return (
                  <div key={mod.id} className={`perm-acc${open ? ' perm-acc--open' : ''}`}>
                    <button type="button" className="perm-acc__head" onClick={() => setExpandedModule(open ? '' : mod.id)}>
                      <span className="perm-acc__head-left"><span className="perm-acc__icon"><Icon name={mod.icon} size={16} color="#fff" /></span><span className="perm-acc__title">{mod.label}</span></span>
                      <Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} color="#78909c" />
                    </button>
                    {open && (
                      <div className="perm-acc__body">
                        <div className="perm-row perm-row--page">
                          <div className="perm-row__label">{t.pageAccess}</div>
                          <label className="perm-switch"><input type="checkbox" checked={p.pageAccess} onChange={(e) => mutate(mod.id, (m) => { m.pageAccess = e.target.checked; })} /><span className="perm-switch__slider" /></label>
                        </div>
                        <div className="perm-sub"><div className="perm-sub__title">{t.actions}</div><div className="perm-chips">{mod.actions.map((a) => <label key={a.id} className={`perm-chip${p.actions[a.id] ? ' perm-chip--on' : ''}`}><input type="checkbox" checked={p.actions[a.id]} disabled={!p.pageAccess} onChange={(e) => mutate(mod.id, (m) => { m.actions[a.id] = e.target.checked; })} /><span>{a.label}</span></label>)}</div></div>
                        <div className="perm-sub"><div className="perm-sub__title">{t.uiItems}</div><div className="perm-chips perm-chips--ui">{mod.uiItems.map((ui) => <label key={ui.id} className={`perm-chip perm-chip--ui${p.ui[ui.id] ? ' perm-chip--on' : ''}`}><input type="checkbox" checked={p.ui[ui.id]} disabled={!p.pageAccess} onChange={(e) => mutate(mod.id, (m) => { m.ui[ui.id] = e.target.checked; })} /><span>{ui.label}</span></label>)}</div></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
        )}
      </div>
    </div>
  );
}
